import { cancelQueryReads } from 'src/modules/shared/utils/api/cancel-query-reads.util';
import type { QueryClient } from '@tanstack/react-query';
import { conversationQueryOptions } from '../utils/api/conversation-query.util';
import {
  conversationRecoveryQueryKey,
  getConversationRecoveryMinimum,
  getConversationRecoveryEnrichment,
} from '../utils/api/conversation-recovery-query.util';
import { recoverConversationDocuments } from './conversation-document-recovery.service';
import { scheduleBackgroundSearch } from './background-search.service';
import { updateFunnelConversationResourceCache } from '../utils/cache/funnel-conversations/update.util';
import { ApiError } from 'src/api/generated/core/ApiError';
import type { CanonicalConversationRecoveryOptions } from '../types/canonical-conversation-recovery.types';
import {
  affectedConversationQueries,
  conversationNewQueryMatcher,
} from '../utils/cache/funnel-conversations/query-impact.util';
import {
  getAcceptedConversation,
  rememberConversationEvent,
  projectKnownConversationRows,
} from '../utils/cache/funnel-conversations/recent-conversation-events.util';

/**
 * Obtains canonical authority when equal-time socket events cannot be ordered.
 * Cancels pre-notification reads, bounds the fresh request and retains polling
 * recovery on denial/failure. No socket arrival order is treated as a revision.
 * @param queryClient - Current account's cache owner.
 * @param conversationId - Authorized CRM conversation identity.
 * @param tenantId - Tenant that must own the returned resource.
 * @param minimumRevision - Canonical conversation revision to reach, when known.
 * Related Message deletions do not create a new conversation revision.
 * @param recoveryOptions - Related enrichment changes requiring independent search recovery.
 * @returns Completion of the best-effort canonical read and search catch-up.
 */
export const recoverCanonicalConversation = async (
  queryClient: QueryClient,
  conversationId: number,
  tenantId: string,
  minimumRevision?: string,
  recoveryOptions: CanonicalConversationRecoveryOptions = {}
): Promise<void> => {
  const options = conversationQueryOptions(conversationId);
  // Operation-specific deadlines must never become the ordinary detail query's policy.
  // Nest the temporary key so accepted socket snapshots/account reset still cancel it.
  const recoveryKey = conversationRecoveryQueryKey(conversationId, tenantId);
  const minimumTimestamp = Math.max(
    Date.parse(minimumRevision ?? '') || 0,
    getConversationRecoveryMinimum(queryClient, conversationId, tenantId)
  );
  const deadline = Date.now() + 30_000;
  const inheritedEnrichment = getConversationRecoveryEnrichment(
    queryClient,
    conversationId,
    tenantId
  );
  const enrichment = recoveryOptions.enrichmentChanged
    ? {
        deadline,
        scope: new Set(
          queryClient.getQueryCache().findAll({
            queryKey: ['crm/documents/funnel-conversations'],
          })
        ),
      }
    : inheritedEnrichment;
  cancelQueryReads(queryClient, { queryKey: options.queryKey });
  try {
    const recovered = await queryClient.fetchQuery({
      ...options,
      queryKey: recoveryKey,
      meta: { minimumRevision: minimumTimestamp, deadline, enrichment },
      gcTime: 0,
      staleTime: 0,
      retry: (failures, error) =>
        failures < 5 &&
        Date.now() < deadline &&
        !(error instanceof ApiError && [401, 403, 404].includes(error.status)),
      retryDelay: (attempt) =>
        Math.min(
          1_000 * 2 ** attempt,
          8_000,
          Math.max(0, deadline - Date.now())
        ),
      queryFn: async (context) => {
        if (Date.now() >= deadline) {
          throw new Error('Canonical recovery deadline exhausted');
        }
        const read = async () => {
          const timeout = window.setTimeout(
            () => {
              void queryClient.cancelQueries(
                { queryKey: recoveryKey, exact: true },
                { revert: false }
              );
            },
            Math.min(5_000, deadline - Date.now())
          );
          try {
            const result = await options.queryFn(context);
            if (result.id !== conversationId || result.tenantId !== tenantId) {
              throw new Error('Canonical conversation identity mismatch');
            }
            if (
              minimumTimestamp > 0 &&
              !(Date.parse(result.updatedAt) >= minimumTimestamp)
            ) {
              throw new Error(
                'Canonical conversation has not reached the notification revision'
              );
            }
            return result;
          } finally {
            window.clearTimeout(timeout);
          }
        };
        if (
          !queryClient
            .getQueryCache()
            .findAll({ queryKey: ['crm/documents/funnel-conversations'] })
            .length
        ) {
          return read();
        }
        const result = await scheduleBackgroundSearch(queryClient, {
          signal: context.signal,
          deadline,
          isRelevant: () => !context.signal.aborted,
          run: read,
        });
        if (!result) throw new Error('Canonical recovery no longer relevant');
        return result;
      },
    });
    const conversation = queryClient.setQueryData<
      Awaited<ReturnType<typeof options.queryFn>>
    >(options.queryKey, (current) =>
      options.structuralSharing(current, recovered)
    );
    if (!conversation || conversation.tenantId !== tenantId) return;
    const previous = getAcceptedConversation(
      queryClient,
      tenantId,
      conversationId
    );
    const protectedProjection = rememberConversationEvent(
      queryClient,
      conversationId,
      tenantId,
      conversation.updatedAt,
      conversation
    );
    const scope = new Set(
      affectedConversationQueries(
        queryClient,
        conversation,
        previous,
        false,
        !protectedProjection
      )
    );
    if (enrichment && Date.now() < enrichment.deadline) {
      affectedConversationQueries(
        queryClient,
        conversation,
        previous,
        false,
        true
      ).forEach((query) => {
        if (enrichment.scope.has(query)) scope.add(query);
      });
    }
    updateFunnelConversationResourceCache(
      queryClient,
      {
        params: { id: conversationId, data: conversation },
      },
      conversation
    );
    if (protectedProjection) {
      projectKnownConversationRows(queryClient, conversation);
    }
    if (scope.size) {
      await recoverConversationDocuments(
        queryClient,
        conversation,
        scope,
        conversationNewQueryMatcher(conversation, previous)
      );
    }
  } catch {
    // Existing visibility/reconnect/polling recovery remains available.
  }
};
